import React from "react";
import {Box, Chip, Divider, Grid, LinearProgress, Paper, Stack, Typography} from "@mui/material";
import {Email, Person} from "@mui/icons-material";
import type {Supplier360} from "../../supplier360DemoData";
import {formatCurrency, formatPercent, riskScoreBand} from "../../supplier360Utils";

type Props = { supplier: Supplier360 };

const ScoreBar: React.FC<{ label: string; value: number; color?: string }> = ({label, value, color}) => (
    <Box mb={1.5}>
        <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={600}>{value}/100</Typography>
        </Box>
        <LinearProgress
            variant="determinate"
            value={value}
            sx={{height: 8, borderRadius: 4, ...(color ? {'& .MuiLinearProgress-bar': {backgroundColor: color}} : {})}}
        />
    </Box>
);

export const OverviewTab: React.FC<Props> = ({supplier}) => {
    const band = riskScoreBand(supplier.riskScore);
    return <Grid container spacing={2}>
        <Grid size={{xs: 12, md: 7}}>
            <Paper variant="outlined" sx={{p: 2.5, borderRadius: '10px', height: '100%'}}>
                <Typography variant="h3" gutterBottom>Supplier profile</Typography>
                <Grid container spacing={2}>
                    {[
                        ['Category', supplier.category],
                        ['Region', `${supplier.country} · ${supplier.region}`],
                        ['Relationship since', String(supplier.relationshipSince)],
                        ['Payment terms', supplier.paymentTerms],
                        ['Annual spend', formatCurrency(supplier.annualSpend, supplier.currency)],
                        ['Spend YoY', formatPercent(supplier.spendTrendPct, true)],
                    ].map(([label, value]) => (
                        <Grid size={{xs: 6}} key={label}>
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                            <Typography variant="body1" fontWeight={600}>{value}</Typography>
                        </Grid>
                    ))}
                </Grid>

                <Box mt={2} mb={1}>
                    <Typography variant="caption" color="text.secondary">Sub-categories</Typography>
                    <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" useFlexGap>
                        {supplier.subCategories.map(c => <Chip key={c} size="small" label={c} variant="outlined"/>)}
                    </Stack>
                </Box>

                <Divider sx={{my: 2}}/>

                <Typography variant="caption" color="text.secondary">Primary contact</Typography>
                <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                    <Person fontSize="small" color="action"/>
                    <Typography variant="body2" fontWeight={600}>{supplier.primaryContact.name}</Typography>
                    <Typography variant="body2" color="text.secondary">· {supplier.primaryContact.role}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                    <Email fontSize="small" color="action"/>
                    <Typography variant="body2" color="text.secondary">{supplier.primaryContact.email}</Typography>
                </Stack>
            </Paper>
        </Grid>

        <Grid size={{xs: 12, md: 5}}>
            <Paper variant="outlined" sx={{p: 2.5, borderRadius: '10px', height: '100%'}}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h3">Performance & risk</Typography>
                    <Chip size="small" color={band.color} label={`${band.label} risk`}/>
                </Box>
                <ScoreBar label="Quality score" value={supplier.qualityScore}/>
                <ScoreBar label="On-time delivery" value={Math.round(supplier.onTimeDeliveryPct * 100)}/>
                <ScoreBar label="ESG score" value={supplier.esgScore}/>
                <ScoreBar label="Risk score" value={supplier.riskScore} color="#FF3435"/>
                <Box mt={2}>
                    <Typography variant="caption" color="text.secondary">
                        Share of category spend
                    </Typography>
                    <Typography variant="h2" fontWeight={700}>
                        {formatPercent(supplier.spendShareOfCategory)}
                    </Typography>
                </Box>
            </Paper>
        </Grid>
    </Grid>;
};
